import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { ChevronDown, ChevronRight, FolderKanban, Plus, Trash2 } from 'lucide-react';
import type { ProjectDto, ProjectHoursReportDto, ProjectTimeEntryDto } from '@workforce/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useEmployeeOptions } from '@/lib/queries';
import { fmtDate } from '@/lib/format';
import { t } from '@/lib/i18n';
import { Button, Card, ConfirmDialog, EmptyState, ErrorBanner, Field, Input, Modal, Notice, PageHeader, Select, Spinner, Textarea } from '@/components/ui';
import { ExportMenu } from '@/components/ExportMenu';

const iso = (d: DateTime) => d.toISODate()!;
const num = (n: number) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

interface EntryForm {
  employeeId: string;
  projectId: string;
  workDate: string;
  hours: string;
  subJob: string;
  note: string;
}
const blankEntry = (): EntryForm => ({ employeeId: '', projectId: '', workDate: iso(DateTime.now()), hours: '', subJob: '', note: '' });

export function ProjectHoursPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [from, setFrom] = useState(iso(DateTime.now().minus({ days: 29 })));
  const [to, setTo] = useState(iso(DateTime.now()));
  const [projectId, setProjectId] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [logging, setLogging] = useState<EntryForm | null>(null);
  const [form, setForm] = useState<EntryForm>(blankEntry());
  const [formErr, setFormErr] = useState<string | null>(null);
  const [del, setDel] = useState<ProjectTimeEntryDto | null>(null);

  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.get<ProjectDto[]>('/projects') });
  const people = useEmployeeOptions(can('projects.write'));
  const report = useQuery({
    queryKey: ['project-hours', from, to, projectId],
    queryFn: () => api.get<ProjectHoursReportDto>(`/projects/report/hours?from=${from}&to=${to}${projectId ? `&projectId=${projectId}` : ''}`),
    enabled: from <= to,
  });
  const entries = useQuery({
    queryKey: ['project-time-entries', from, to, projectId],
    queryFn: () => api.get<ProjectTimeEntryDto[]>(`/projects/time/entries?from=${from}&to=${to}${projectId ? `&projectId=${projectId}` : ''}&limit=200`),
    enabled: from <= to,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['project-hours'] });
    qc.invalidateQueries({ queryKey: ['project-time-entries'] });
  };

  const create = useMutation({
    mutationFn: (f: EntryForm) =>
      api.post('/projects/time/entries', {
        employeeId: f.employeeId,
        projectId: f.projectId,
        workDate: f.workDate,
        hours: Number(f.hours),
        subJob: f.subJob.trim() || undefined,
        note: f.note.trim() || undefined,
      }),
    onSuccess: () => {
      setLogging(null);
      refresh();
    },
    onError: (e) => setFormErr(e instanceof ApiError ? e.message : t('Could not save')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/projects/time/entries/${id}`),
    onSuccess: () => {
      setDel(null);
      refresh();
    },
  });

  const toggle = (key: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const submit = () => {
    setFormErr(null);
    if (!form.employeeId || !form.projectId || !form.workDate) return setFormErr(t('Choose an employee, a project and a date.'));
    if (!(Number(form.hours) > 0)) return setFormErr(t('Enter the hours worked.'));
    create.mutate(form);
  };

  const activeProjects = useMemo(() => (projects.data ?? []).filter((p) => p.status !== 'completed'), [projects.data]);

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('Project hours')}
        subtitle={t('Hours logged against each project, per engineer. Use the report to see where time went.')}
        actions={
          <div className="flex items-center gap-2">
            <ExportMenu kind="project-hours" params={{ from, to }} label={t('Export')} />
            {can('projects.write') && (
              <Button icon={<Plus className="h-4 w-4" />} onClick={() => { setForm(blankEntry()); setFormErr(null); setLogging(blankEntry()); }}>
                {t('Log hours')}
              </Button>
            )}
          </div>
        }
      />

      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label={t('From')}>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label={t('To')}>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field label={t('Project')}>
            <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">{t('All projects')}</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      {report.isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : report.error ? (
        <ErrorBanner error={report.error} />
      ) : !report.data || report.data.rows.length === 0 ? (
        <EmptyState icon={<FolderKanban className="h-8 w-8" />} title={t('No hours logged for these dates')} description={can('projects.write') ? t('Use "Log hours" to add time against a project.') : undefined} />
      ) : (
        <Card title={t('Hours by project')}>
          <p className="mb-3 text-sm text-slate-500">{t('{n} hours in total', { n: num(report.data.totalHours) })}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="py-2 text-start font-medium">{t('Project')}</th>
                  <th className="py-2 text-start font-medium">{t('Sub-job')}</th>
                  <th className="py-2 text-end font-medium">{t('People')}</th>
                  <th className="py-2 text-end font-medium">{t('Entries')}</th>
                  <th className="py-2 text-end font-medium">{t('Total hours')}</th>
                </tr>
              </thead>
              <tbody>
                {report.data.rows.map((r) => {
                  const key = `${r.projectId}::${r.subJob ?? ''}`;
                  const open = expanded.has(key);
                  return (
                    <>
                      <tr key={key} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2">
                          <button className="flex items-center gap-1 font-medium text-slate-900" onClick={() => toggle(key)}>
                            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            {r.projectName}
                            {r.projectCode && <span className="text-xs text-slate-400">({r.projectCode})</span>}
                          </button>
                        </td>
                        <td className="py-2 text-slate-600">{r.subJob ?? <span className="text-slate-400">{t('No sub-job')}</span>}</td>
                        <td className="py-2 text-end tabular-nums">{r.people.length}</td>
                        <td className="py-2 text-end tabular-nums">{r.entries}</td>
                        <td className="py-2 text-end font-semibold tabular-nums">{num(r.totalHours)}</td>
                      </tr>
                      {open &&
                        r.people.map((p) => (
                          <tr key={key + p.employeeId} className="border-b border-slate-100 bg-slate-50/50 text-slate-600">
                            <td className="py-1.5 ps-7">{p.employeeName} <span className="text-xs text-slate-400">{p.employeeCode}</span></td>
                            <td />
                            <td />
                            <td className="py-1.5 text-end tabular-nums">{p.entries}</td>
                            <td className="py-1.5 text-end tabular-nums">{num(p.hours)}</td>
                          </tr>
                        ))}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {can('projects.write') && entries.data && entries.data.length > 0 && (
        <Card title={t('Logged entries')}>
          <p className="mb-3 text-sm text-slate-500">{t('Most recent first — remove any mistakes here.')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="py-2 text-start font-medium">{t('Date')}</th>
                  <th className="py-2 text-start font-medium">{t('Employee')}</th>
                  <th className="py-2 text-start font-medium">{t('Project')}</th>
                  <th className="py-2 text-start font-medium">{t('Sub-job')}</th>
                  <th className="py-2 text-end font-medium">{t('Hours')}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {entries.data.map((e) => (
                  <tr key={e.id} className="border-b border-slate-100">
                    <td className="py-1.5">{fmtDate(e.workDate)}</td>
                    <td className="py-1.5">{e.employeeName}</td>
                    <td className="py-1.5">{e.projectName}</td>
                    <td className="py-1.5 text-slate-500">{e.subJob ?? '—'}</td>
                    <td className="py-1.5 text-end tabular-nums">{num(e.hours)}</td>
                    <td className="py-1.5 text-end">
                      <Button variant="ghost" size="sm" aria-label={t('Remove')} onClick={() => setDel(e)}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {logging && (
        <Modal open title={t('Log hours')} onClose={() => setLogging(null)}>
          <div className="space-y-3">
            {formErr && <Notice tone="amber">{formErr}</Notice>}
            <Field label={t('Employee')}>
              <Select value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}>
                <option value="">{t('Choose…')}</option>
                {(people.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>{p.fullName} ({p.employeeCode})</option>
                ))}
              </Select>
            </Field>
            <Field label={t('Project')}>
              <Select value={form.projectId} onChange={(e) => setForm({ ...form, projectId: e.target.value })}>
                <option value="">{t('Choose…')}</option>
                {activeProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('Date')}>
                <Input type="date" value={form.workDate} onChange={(e) => setForm({ ...form, workDate: e.target.value })} />
              </Field>
              <Field label={t('Hours')}>
                <Input type="number" step="0.25" min="0" max="24" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} />
              </Field>
            </div>
            <Field label={t('Sub-job')} hint={t('Optional — e.g. a phase or task within the project.')}>
              <Input value={form.subJob} onChange={(e) => setForm({ ...form, subJob: e.target.value })} />
            </Field>
            <Field label={t('Note')} hint={t('Optional.')}>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={() => setLogging(null)}>{t('Cancel')}</Button>
              <Button loading={create.isPending} onClick={submit}>{t('Save')}</Button>
            </div>
          </div>
        </Modal>
      )}

      {del && (
        <ConfirmDialog
          open
          danger
          title={t('Remove this entry?')}
          message={t('{hours} h for {name} on {project} will be deleted.', { hours: num(del.hours), name: del.employeeName, project: del.projectName })}
          confirmLabel={t('Remove')}
          onConfirm={() => remove.mutate(del.id)}
          onClose={() => setDel(null)}
          loading={remove.isPending}
        />
      )}
    </div>
  );
}
